import './reset.css';

import React from 'react';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { LocalizeProvider } from 'react-localize-redux';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import store, { history } from './store';

import { setup as setupSDK } from './sdk';
import MainRouter from './router';
import LoadingModalCtrl from './controllers/loader-modal';
import ModalDialogsCtrl from './controllers/modal-dialogs';
import PageTitle from '@components/basics/page-title';
import { ThemeProvider } from 'styled-components';
import theme from './theme';
import ThemeWrapper from '@components/theme/theme-wrapper';
import { rudderInitialize } from './utils/rudderstack/init';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import useWindowDimensions from './utils/windows-dimensions';
import TracProMenu from './pages/artist-profile/artist-subscriptions/trac-pro-menu';
import NotificationsMenu from '@webapp/pages/notifications/notifications-menu';
import VerificationMenu from '@webapp/pages/dashboard/verification-menu';
import ReleaseApprovedMenu from '@webapp/pages/release/release-approved-menu';
import SuccessSubscriptionMenu from '@webapp/components/success-subscription-menu';
import SyncNotificationMenu from '@webapp/pages/notifications/sync-notification-menu';
import { initBeacon } from './utils/helpscout/init';
import DealDetailsMenu from './controllers/menus/deal-details-menu';

setupSDK();
rudderInitialize();
initBeacon();

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string
);

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        'https://fonts.googleapis.com/css2?family=Sen:wght@400;700&display=swap',
    },
  ],
};

const App = () => {
  const { height } = useWindowDimensions();

  React.useEffect(() => {
    return document.documentElement.style.setProperty(
      '--app-height',
      `${height}px`
    );
  }, [height]);

  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <React.StrictMode>
          <DndProvider backend={Backend}>
            <LocalizeProvider>
              <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
                <ThemeProvider theme={theme}>
                  <ThemeWrapper>
                    <PageTitle />
                    <LoadingModalCtrl />
                    <ModalDialogsCtrl />
                    <MainRouter />
                    <TracProMenu noButton />
                    <NotificationsMenu noButton />
                    <VerificationMenu noButton />
                    <ReleaseApprovedMenu noButton />
                    <SyncNotificationMenu noButton />
                    <SuccessSubscriptionMenu type={'distribution'} />
                    <DealDetailsMenu />
                  </ThemeWrapper>
                </ThemeProvider>
              </Elements>
            </LocalizeProvider>
          </DndProvider>
        </React.StrictMode>
      </ConnectedRouter>
    </Provider>
  );
};

export default Sentry.withProfiler(App);
